/**
 * Movimientos de inventario que no son ni compra ni venta.
 *
 * Between them these close the gap that otherwise makes stock counts meaningless: product that
 * was repackaged, counted, damaged, eaten, stolen, or moved to the truck. Without somewhere to
 * record each of those, the shelf and the ledger drift apart permanently and nobody can say
 * which one is wrong.
 */

import {
  ZERO,
  ZERO_COST,
  ZERO_QTY,
  addMoney,
  addQty,
  centavos,
  costOf,
  esCamion,
  fechaEnNicaragua,
  milli,
  orderKey,
  repartirProporcional,
  subMoney,
  subQty,
  unitCostFromTotal,
  weightedAverageCost,
  type AnyDomainEvent,
  type Centavos,
  type Milli,
  type UnitCost,
} from '@abastece/shared';
import { credit, debit, entry, type JournalLine } from '../ledger/journal.js';
import type { AccountKey } from '../ledger/accounts.js';
import { ajusteVendidoAntes, consumirLotesFefo, cuentaInventario, devolverLotes, entradaYaContada, momentoDe, yaContado, type Recibido } from '../inventario.js';
import {
  existenciaId,
  loteId,
  type ApplyResult,
  type Conteo,
  type Effect,
  type Existencia,
  type MotivoSalida,
  type Reader,
  type Reempaque,
  type Salida,
  type Traslado,
} from '../state.js';

const CUENTA_SALIDA: Record<MotivoSalida, AccountKey> = {
  merma: 'MERMA',
  vencido: 'VENCIDOS',
  consumo_interno: 'CONSUMO_INTERNO',
  robo: 'FALTANTE_INVENTARIO',
};

export async function reduceInventario(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'inventario.reempaque':
      return reempaque(event, read, rev);
    case 'inventario.conteo':
      return conteo(event, read, rev);
    case 'inventario.salida':
      return salida(event, read, rev);
    case 'inventario.traslado':
      return traslado(event, read, rev);
    case 'inventario.costo_corregido':
      return costoCorregido(event, read, rev);
    default:
      return null;
  }
}

function fijarExistencia(
  productoId: string,
  ubicacion: string,
  cantidad: Milli,
  costoPromedio: UnitCost,
  movimientoId: string,
  rev: number,
  /** Only from a count: when it established this stock. */
  contadoEn?: string,
): Effect {
  const id = existenciaId(productoId, ubicacion);
  const row: Existencia = {
    id,
    productoId,
    ubicacion,
    cantidad,
    costoPromedio,
    ultimoMovimientoId: movimientoId,
    rev,
    ...(contadoEn ? { contadoEn } : {}),
  };
  return { kind: 'existencia.set', id, row };
}

// ---------------------------------------------------------------------------
// Reempaque
// ---------------------------------------------------------------------------

async function reempaque(
  event: Extract<AnyDomainEvent, { type: 'inventario.reempaque' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  const origen = await read.producto(p.origenProductoId);
  if (!origen) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'El producto de origen no existe' }] };
  }
  for (const d of p.destinos) {
    if (!(await read.producto(d.productoId))) {
      return { effects: [], rejections: [{ eventId: event.id, reason: 'Un producto de destino no existe' }] };
    }
  }

  const actualOrigen = await read.existencia(p.origenProductoId, p.ubicacion);
  const estadoOrigen = { cantidad: actualOrigen?.cantidad ?? ZERO_QTY, costoPromedio: actualOrigen?.costoPromedio ?? ZERO_COST };
  /**
   * Repacked before a count that reached the books first: the shelf the count saw already had it
   * repacked, so the stock stays as counted, and the value goes against what that count found
   * («Faltante de inventario», `yaContado`), as a sale's does.
   */
  const origenContado = yaContado(actualOrigen, event);
  const consumido = p.origenCantidad as Milli;
  const merma = p.mermaCantidad as Milli;

  const valorConsumido = costOf(estadoOrigen.costoPromedio, consumido);
  const valorMerma = costOf(estadoOrigen.costoPromedio, merma);
  const costoAdicional = centavos(p.costoAdicional);

  /**
   * Everything the source was worth, less what was lost, plus what the bags and labour cost,
   * lands on the output. That is what makes the packaged product's cost tell the truth — and
   * what eventually shows the owner that bagging rice costs a pound a sack.
   */
  const valorBueno = addMoney(subMoney(valorConsumido, valorMerma), costoAdicional);
  /*
   * The same product twice among the destinations is one destination with both added: each used to
   * be read from the stock as it stood before the event, and the second wrote over the first, which
   * vanished from the shelf and not from the books (2026-10-13).
   */
  const juntos = new Map<string, { productoId: string; cantidad: number; consumido?: number }>();
  for (const d of p.destinos) {
    const antes = juntos.get(d.productoId);
    juntos.set(d.productoId, {
      productoId: d.productoId,
      cantidad: (antes?.cantidad ?? 0) + d.cantidad,
      ...(d.consumido !== undefined ? { consumido: (antes?.consumido ?? 0) + d.consumido } : {}),
    });
  }
  const destinosJuntos = [...juntos.values()];
  // By what of the source went into each, when it says (`consumido`); otherwise by the quantities.
  const partes = repartirProporcional(
    valorBueno,
    destinosJuntos.map((d) => d.consumido ?? d.cantidad),
  );

  const effects: Effect[] = [];
  if (!origenContado) {
    effects.push(
      fijarExistencia(
        p.origenProductoId,
        p.ubicacion,
        subQty(estadoOrigen.cantidad, consumido),
        estadoOrigen.costoPromedio,
        p.reempaqueId,
        rev,
      ),
    );
    const lotesOrigen = await read.lotesDe(p.origenProductoId, p.ubicacion);
    effects.push(...consumirLotesFefo(lotesOrigen, consumido, rev));
  }

  const destinos: Reempaque['destinos'] = [];
  const empacados: Recibido[] = [];
  /** What went onto shelves a count had already found it on (`entradaYaContada`): into their worth, and against what it found over. */
  let aEstanteContado: Centavos = ZERO;
  let aFaltanteContado: Centavos = ZERO;
  for (let i = 0; i < destinosJuntos.length; i++) {
    const d = destinosJuntos[i]!;
    const valor = partes[i]!;
    const cantidad = d.cantidad as Milli;
    destinos.push({ productoId: d.productoId, cantidad, valorAsignado: valor });
    const actual = await read.existencia(d.productoId, p.ubicacion);
    if (actual && yaContado(actual, event)) {
      const ya = entradaYaContada(actual, cantidad, valor, p.reempaqueId, rev);
      if (ya.efecto) effects.push(ya.efecto);
      aEstanteContado = addMoney(aEstanteContado, ya.aEstante);
      aFaltanteContado = addMoney(aFaltanteContado, ya.aFaltante);
      continue;
    }
    const estado = { cantidad: actual?.cantidad ?? ZERO_QTY, costoPromedio: actual?.costoPromedio ?? ZERO_COST };
    const llegada = {
      cantidad: addQty(estado.cantidad, cantidad),
      costoPromedio: weightedAverageCost(estado.cantidad, estado.costoPromedio, cantidad, unitCostFromTotal(valor, cantidad)),
    };
    empacados.push({ antes: estado, despues: llegada, entrante: valor, cuenta: cuentaInventario(p.ubicacion) });

    effects.push(fijarExistencia(d.productoId, p.ubicacion, llegada.cantidad, llegada.costoPromedio, p.reempaqueId, rev));
  }

  const cuenta = cuentaInventario(p.ubicacion);
  const lineas: JournalLine[] = [
    origenContado ? credit('FALTANTE_INVENTARIO', valorConsumido, 'Ya contado') : credit(cuenta, valorConsumido, origen.nombre),
  ];
  if (valorMerma > 0) lineas.push(debit('MERMA', valorMerma, 'Pérdida en reempaque'));
  const aEstante = subMoney(valorBueno, aFaltanteContado);
  if (aEstante > 0) lineas.push(debit(cuenta, aEstante, 'Producto empacado'));
  if (aEstante < 0) lineas.push(credit(cuenta, centavos(-aEstante), 'Producto empacado'));
  if (aFaltanteContado > 0) lineas.push(debit('FALTANTE_INVENTARIO', aFaltanteContado, 'Ya contado'));
  if (aFaltanteContado < 0) lineas.push(credit('FALTANTE_INVENTARIO', centavos(-aFaltanteContado), 'Ya contado'));
  if (costoAdicional > 0) lineas.push(credit('CAJA_FUERTE', costoAdicional, 'Empaque y mano de obra'));

  effects.push({
    kind: 'asiento',
    entry: entry(event.id, p.fecha, `Reempaque de ${origen.nombre}`, lineas),
  });
  const ajusteEmpaque = ajusteVendidoAntes(event.id, p.fecha, empacados);
  if (ajusteEmpaque) effects.push(ajusteEmpaque);

  const fila: Reempaque = {
    id: p.reempaqueId,
    ubicacion: p.ubicacion,
    fecha: p.fecha,
    origenProductoId: p.origenProductoId,
    origenCantidad: consumido,
    mermaCantidad: merma,
    destinos,
    costoAdicional,
    valorConsumido,
    valorMerma,
    rev,
  };
  effects.push({ kind: 'reempaque.upsert', id: p.reempaqueId, row: fila });

  return { effects, rejections: [] };
}

// ---------------------------------------------------------------------------
// Conteo físico
// ---------------------------------------------------------------------------

/**
 * A physical count. Each line says what was on the shelf and what the books said on the counting
 * device when it was counted, and the difference is what moves. Setting the counted number
 * instead undid every sale rung up between counting a line and saving the count, and those came
 * back as a shortfall at the next count: noise that hides real loss.
 *
 * A movement from before a line was counted that reaches the books after it is taken in where it
 * lands (`yaContado`). A line counted before a count of the same product that is already in
 * changes nothing: that later count found whatever this one did.
 */
async function conteo(
  event: Extract<AnyDomainEvent, { type: 'inventario.conteo' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const effects: Effect[] = [];
  const lineas: Conteo['lineas'] = [];

  let valorFaltante: Centavos = ZERO;
  let valorSobrante: Centavos = ZERO;

  // The same product twice: the later line is the count.
  const porProducto = new Map<string, (typeof p.lineas)[number]>();
  for (const l of p.lineas) {
    const antes = porProducto.get(l.productoId);
    if (!antes || Date.parse(l.contadoEn) >= Date.parse(antes.contadoEn)) porProducto.set(l.productoId, l);
  }

  for (const l of porProducto.values()) {
    if (!(await read.producto(l.productoId))) continue;

    const actual = await read.existencia(l.productoId, p.ubicacion);
    const estado = { cantidad: actual?.cantidad ?? ZERO_QTY, costoPromedio: actual?.costoPromedio ?? ZERO_COST };
    const contadoEn = new Date(momentoDe(event, l.contadoEn)).toISOString();
    const superada = actual?.contadoEn != null && Date.parse(contadoEn) <= Date.parse(actual.contadoEn);
    const contada = l.cantidadContada as Milli;
    const sistema = l.cantidadSistema as Milli;
    const diferencia = superada ? ZERO_QTY : subQty(contada, sistema);
    const valor = costOf(estado.costoPromedio, milli(Math.abs(diferencia)));

    if (diferencia < 0) valorFaltante = addMoney(valorFaltante, valor);
    if (diferencia > 0) valorSobrante = addMoney(valorSobrante, valor);

    // Cost per unit does not change — only how many there are.
    if (!superada) {
      effects.push(
        fijarExistencia(
          l.productoId,
          p.ubicacion,
          addQty(estado.cantidad, diferencia),
          estado.costoPromedio,
          p.conteoId,
          rev,
          contadoEn,
        ),
      );
    }

    if (diferencia !== 0) {
      const lotes = await read.lotesDe(l.productoId, p.ubicacion);
      effects.push(
        ...(diferencia < 0
          ? consumirLotesFefo(lotes, milli(-diferencia), rev)
          : devolverLotes(lotes, diferencia, rev)),
      );
    }

    lineas.push({
      productoId: l.productoId,
      cantidadContada: contada,
      cantidadSistema: sistema,
      diferencia,
      valorDiferencia: diferencia < 0 ? centavos(-valor) : valor,
      contadoEn,
      ...(superada ? { superada: true } : {}),
    });
  }

  if (lineas.length === 0) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ningún producto del conteo existe' }] };
  }

  const cuenta = cuentaInventario(p.ubicacion);
  const asiento: JournalLine[] = [];
  if (valorFaltante > 0) {
    asiento.push(debit('FALTANTE_INVENTARIO', valorFaltante), credit(cuenta, valorFaltante));
  }
  if (valorSobrante > 0) {
    asiento.push(debit(cuenta, valorSobrante), credit('SOBRANTE_INVENTARIO', valorSobrante));
  }
  if (asiento.length > 0) {
    effects.push({
      kind: 'asiento',
      entry: entry(event.id, p.fecha, `Conteo físico${p.nota ? `: ${p.nota}` : ''}`, asiento),
    });
  }

  effects.push({
    kind: 'conteo.upsert',
    id: p.conteoId,
    row: {
      id: p.conteoId,
      ubicacion: p.ubicacion,
      fecha: p.fecha,
      lineas,
      valorFaltante,
      valorSobrante,
      rev,
    },
  });

  return { effects, rejections: [] };
}

// ---------------------------------------------------------------------------
// Salida sin venta
// ---------------------------------------------------------------------------

async function salida(
  event: Extract<AnyDomainEvent, { type: 'inventario.salida' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  const producto = await read.producto(p.productoId);
  if (!producto) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'El producto no existe' }] };
  }

  const actual = await read.existencia(p.productoId, p.ubicacion);
  const estado = { cantidad: actual?.cantidad ?? ZERO_QTY, costoPromedio: actual?.costoPromedio ?? ZERO_COST };
  const cantidad = p.cantidadBase as Milli;
  const costo = costOf(estado.costoPromedio, cantidad);
  /**
   * Written off before a count that reached the books first — a phone with no signal: the count
   * already found it gone and booked it missing. Taken off again, the shelf read two short. The
   * stock stays as counted, and what the count booked missing turns out to be this (`yaContado`).
   */
  const contado = yaContado(actual, event);

  const effects: Effect[] = contado
    ? []
    : [fijarExistencia(p.productoId, p.ubicacion, subQty(estado.cantidad, cantidad), estado.costoPromedio, p.salidaId, rev)];

  // A count that took it in took its difference out of the batches already.
  if (!contado) {
    const lotes = await read.lotesDe(p.productoId, p.ubicacion);
    if (p.loteId) {
      // Writing off one specific expired batch, not whatever expires soonest.
      const lote = lotes.find((x) => x.id === p.loteId);
      if (lote) {
        effects.push({
          kind: 'lote.upsert',
          id: lote.id,
          row: { ...lote, cantidadDisponible: subQty(lote.cantidadDisponible, cantidad), rev },
        });
      }
    } else {
      effects.push(...consumirLotesFefo(lotes, cantidad, rev));
    }
  }

  if (costo > 0) {
    effects.push({
      kind: 'asiento',
      entry: entry(
        event.id,
        p.fecha,
        `${producto.nombre}: ${p.motivo.replace(/_/g, ' ')}${p.nota ? ` — ${p.nota}` : ''}`,
        [debit(CUENTA_SALIDA[p.motivo], costo), contado ? credit('FALTANTE_INVENTARIO', costo, 'Ya contado') : credit(cuentaInventario(p.ubicacion), costo)],
      ),
    });
  }

  const fila: Salida = {
    id: p.salidaId,
    ubicacion: p.ubicacion,
    fecha: p.fecha,
    productoId: p.productoId,
    cantidadBase: cantidad,
    motivo: p.motivo,
    loteId: p.loteId ?? null,
    nota: p.nota ?? null,
    costo,
    rev,
  };
  effects.push({ kind: 'salida.upsert', id: p.salidaId, row: fila });

  return { effects, rejections: [] };
}

// ---------------------------------------------------------------------------
// Traslado
// ---------------------------------------------------------------------------

async function traslado(
  event: Extract<AnyDomainEvent, { type: 'inventario.traslado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  if (p.origen === p.destino) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'El origen y el destino son el mismo' }] };
  }

  const effects: Effect[] = [];
  const lineas: Traslado['lineas'] = [];
  let valorTotal: Centavos = ZERO;
  const recibidos: Recibido[] = [];
  /**
   * Each side moved before a count of it that reached the books first stays as counted, and its
   * value goes against what that count found (`yaContado`): the origin's shelf already short of
   * it, the destination's already holding it.
   */
  let salioContado: Centavos = ZERO;
  /** Of what the destination's count already had, what goes against what it found over (`entradaYaContada`). */
  let llegoContado: Centavos = ZERO;

  // The same product twice is one line with both added: the second used to write over the first.
  const juntas = new Map<string, { productoId: string; cantidadBase: number }>();
  for (const l of p.lineas) {
    juntas.set(l.productoId, { productoId: l.productoId, cantidadBase: (juntas.get(l.productoId)?.cantidadBase ?? 0) + l.cantidadBase });
  }

  for (const l of juntas.values()) {
    if (!(await read.producto(l.productoId))) {
      return { effects: [], rejections: [{ eventId: event.id, reason: 'Un producto del traslado no existe' }] };
    }

    const cantidad = l.cantidadBase as Milli;
    const actualDesde = await read.existencia(l.productoId, p.origen);
    const actualHacia = await read.existencia(l.productoId, p.destino);
    const desde = { cantidad: actualDesde?.cantidad ?? ZERO_QTY, costoPromedio: actualDesde?.costoPromedio ?? ZERO_COST };
    const hacia = { cantidad: actualHacia?.cantidad ?? ZERO_QTY, costoPromedio: actualHacia?.costoPromedio ?? ZERO_COST };
    const origenContado = yaContado(actualDesde, event);
    const destinoContado = yaContado(actualHacia, event);
    const costo = costOf(desde.costoPromedio, cantidad);
    if (origenContado) salioContado = addMoney(salioContado, costo);
    else effects.push(fijarExistencia(l.productoId, p.origen, subQty(desde.cantidad, cantidad), desde.costoPromedio, p.trasladoId, rev));
    if (actualHacia && destinoContado) {
      const ya = entradaYaContada(actualHacia, cantidad, costo, p.trasladoId, rev);
      if (ya.efecto) effects.push(ya.efecto);
      llegoContado = addMoney(llegoContado, ya.aFaltante);
    } else {
      const llegada = {
        cantidad: addQty(hacia.cantidad, cantidad),
        costoPromedio: weightedAverageCost(hacia.cantidad, hacia.costoPromedio, cantidad, desde.costoPromedio),
      };
      recibidos.push({ antes: hacia, despues: llegada, entrante: costo, cuenta: cuentaInventario(p.destino) });
      effects.push(fijarExistencia(l.productoId, p.destino, llegada.cantidad, llegada.costoPromedio, p.trasladoId, rev));
    }

    /**
     * Batches travel with the goods. Leaving them behind would mean FEFO finds nothing at the
     * destination and the expiry board points at the wrong shelf. A side a count already took in
     * keeps the batches that count left it.
     */
    const lotesOrigen = await read.lotesDe(l.productoId, p.origen);
    const salientes = origenContado ? [] : consumirLotesFefo(lotesOrigen, cantidad, rev);
    effects.push(...salientes);

    const lotesDestino = await read.lotesDe(l.productoId, p.destino);
    for (const efecto of destinoContado ? [] : salientes) {
      const anterior = lotesOrigen.find((x) => x.id === efecto.id)!;
      const movido = subQty(anterior.cantidadDisponible, efecto.row.cantidadDisponible);
      if (movido <= 0) continue;

      const id = loteId(l.productoId, anterior.numero, p.destino);
      const existente = lotesDestino.find((x) => x.id === id);
      effects.push({
        kind: 'lote.upsert',
        id,
        row: {
          id,
          productoId: l.productoId,
          numero: anterior.numero,
          vencimiento: anterior.vencimiento,
          cantidadRecibida: addQty(existente?.cantidadRecibida ?? ZERO_QTY, movido),
          cantidadDisponible: addQty(existente?.cantidadDisponible ?? ZERO_QTY, movido),
          compraId: anterior.compraId,
          ubicacion: p.destino,
          rev,
        },
      });
    }

    lineas.push({ productoId: l.productoId, cantidadBase: cantidad, costo });
    valorTotal = addMoney(valorTotal, costo);
  }

  const cuentaOrigen = cuentaInventario(p.origen);
  const cuentaDestino = cuentaInventario(p.destino);

  // Shop floor to storeroom is the same account: the goods moved, the balance sheet did not.
  const neto = new Map<AccountKey, number>();
  const sumar = (cuenta: AccountKey, monto: number) => neto.set(cuenta, (neto.get(cuenta) ?? 0) + monto);
  sumar(cuentaDestino, valorTotal - llegoContado);
  sumar('FALTANTE_INVENTARIO', llegoContado);
  sumar(cuentaOrigen, -(valorTotal - salioContado));
  sumar('FALTANTE_INVENTARIO', -salioContado);
  const lineasTraslado = [...neto]
    .filter(([, monto]) => monto !== 0)
    .map(([cuenta, monto]) =>
      monto > 0
        ? debit(cuenta, centavos(monto), cuenta === 'FALTANTE_INVENTARIO' ? 'Ya contado' : undefined)
        : credit(cuenta, centavos(-monto), cuenta === 'FALTANTE_INVENTARIO' ? 'Ya contado' : undefined),
    );
  if (lineasTraslado.length > 0) {
    effects.push({
      kind: 'asiento',
      entry: entry(event.id, p.fecha, `Traslado ${p.origen} → ${p.destino}`, lineasTraslado),
    });
  }
  const ajuste = ajusteVendidoAntes(event.id, p.fecha, recibidos);
  if (ajuste) effects.push(ajuste);

  effects.push({
    kind: 'traslado.upsert',
    id: p.trasladoId,
    row: {
      id: p.trasladoId,
      origen: p.origen,
      destino: p.destino,
      fecha: p.fecha,
      lineas,
      valorTotal,
      rev,
    },
  });

  /**
   * A transfer touching a truck on an open run is also a load or an early return. Recording it
   * on the jornada is what lets settlement show "went out with C$X, brought back C$Y" without
   * anyone having to reconstruct the day from transfer slips.
   */
  if (valorTotal > 0) {
    const cargado = esCamion(p.destino) ? await read.jornadaAbiertaDe(p.destino) : null;
    if (cargado) {
      effects.push({
        kind: 'jornada.patch',
        id: cargado.id,
        rev,
        patch: { valorCargado: addMoney(cargado.valorCargado, valorTotal) },
      });
    }

    const devuelto = esCamion(p.origen) ? await read.jornadaAbiertaDe(p.origen) : null;
    if (devuelto) {
      effects.push({
        kind: 'jornada.patch',
        id: devuelto.id,
        rev,
        patch: { valorDevuelto: addMoney(devuelto.valorDevuelto, valorTotal) },
      });
    }
  }

  return { effects, rejections: [] };
}

// ---------------------------------------------------------------------------
// Costo corregido
// ---------------------------------------------------------------------------

/**
 * The owner sets what one unit of a product costs in one place (`inventario.costo_corregido`,
 * 2026-10-13). The quantity stays; the stock is worth what the new cost says, and what that changes
 * goes to «Ajuste del costo de inventario», exactly what the row's value changes by, so the shelf
 * and the books keep agreeing. Sales already made keep what they cost; what goes out from here on
 * goes at this cost. It is the stock's last movement: a purchase voided after it would take out
 * what that purchase added, and the stock is worth something else now.
 */
async function costoCorregido(
  event: Extract<AnyDomainEvent, { type: 'inventario.costo_corregido' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const producto = await read.producto(p.productoId);
  if (!producto) return rechazo('El producto no existe');
  const actual = await read.existencia(p.productoId, p.ubicacion);
  const costo = p.costoUnitario as UnitCost;
  if ((actual?.costoPromedio ?? ZERO_COST) === costo) return rechazo('Ese ya es su costo');

  const cantidad = actual?.cantidad ?? ZERO_QTY;
  const diferencia = subMoney(costOf(costo, cantidad), costOf(actual?.costoPromedio ?? ZERO_COST, cantidad));
  const id = existenciaId(p.productoId, p.ubicacion);
  const effects: Effect[] = [
    {
      kind: 'existencia.set',
      id,
      row: {
        ...(actual ?? { id, productoId: p.productoId, ubicacion: p.ubicacion, cantidad }),
        costoPromedio: costo,
        ultimoMovimientoId: event.id,
        rev,
      },
    },
  ];
  if (diferencia !== 0) {
    const monto = centavos(Math.abs(diferencia));
    const cuenta = cuentaInventario(p.ubicacion);
    effects.push({
      kind: 'asiento',
      entry: entry(
        event.id,
        fechaEnNicaragua(event.clientTs),
        `Costo corregido: ${producto.nombre} — ${p.motivo}`,
        diferencia > 0
          ? [debit(cuenta, monto), credit('AJUSTE_COSTO_INVENTARIO', monto)]
          : [debit('AJUSTE_COSTO_INVENTARIO', monto), credit(cuenta, monto)],
      ),
    });
  }
  return { effects, rejections: [] };
}
